<script lang="ts">
  import {
    productDetailStore,
    patchProduct,
    productUsageStore,
    productProblemLinksStore,
  } from "@entities/product/store.svelte";
  import { problemOptionsStore } from "@entities/problem/store.svelte";
  import { sessionStore } from "@features/auth/session/model/store.svelte";
  import InlineField from "@shared/ui/InlineField.svelte";
  import KebabMenu from "@shared/ui/KebabMenu.svelte";
  import AddProblemLinkModal from "@features/product-problem-link-add/AddProblemLinkModal.svelte";
  import ProductCompetitorsEditor from "@features/product-competitors-edit/ProductCompetitorsEditor.svelte";
  import ProductSalesMaterialsEditor from "@widgets/product-sales-materials/ProductSalesMaterialsEditor.svelte";
  import { canEditMasters } from "@shared/types/session";
  import { formatDate } from "@shared/util/format";
  import type { ProductProblemLink } from "@shared/types/product-problem-link";
  import type { ProductPatchRequest } from "@shared/types/product";
  import type { Problem } from "@shared/types/problem";
  import {
    usageRows,
    showOpportunityColumn,
    usageEmptyMessage,
    lostSourceLabel,
    lostReasonLabel,
    verificationStatusLabel,
    listLabel,
    scoreLabel,
    lostDate,
    formatPrice,
  } from "@domain/product/usage-display";
  import type { UsageSegment } from "@domain/product/usage-display";

  interface Props {
    id: string;
  }

  let { id }: Props = $props();

  type TabKey =
    | "overview"
    | "problems"
    | "sales"
    | "competitors"
    | "usage";
  const tabKeys: readonly TabKey[] = [
    "overview",
    "problems",
    "sales",
    "competitors",
    "usage",
  ];
  let activeTab = $state<TabKey>("overview");
  let usageSegment = $state<UsageSegment>("active");

  let addLinkOpen = $state(false);

  let selectedProblemOptions = $state<Problem[]>([]);
  let notesDraft = $state<Record<string, string>>({});
  let busyProblemIds = $state<Set<string>>(new Set());

  const problemsById = $derived(
    new Map(
      [...problemOptionsStore.data, ...selectedProblemOptions].map((p) => [
        p.id,
        p,
      ]),
    ),
  );

  function problemName(pid: string): string {
    return problemsById.get(pid)?.name ?? pid;
  }

  function setBusy(pid: string, busy: boolean) {
    const next = new Set(busyProblemIds);
    if (busy) next.add(pid);
    else next.delete(pid);
    busyProblemIds = next;
  }

  function isTabKey(value: string | null): value is TabKey {
    return value !== null && tabKeys.includes(value as TabKey);
  }

  let linksError = $state<string | null>(null);

  async function commitNotes(link: ProductProblemLink) {
    const next = notesDraft[link.problem_id];
    if (next === undefined) return;
    if (next === (link.notes ?? "")) return;
    setBusy(link.problem_id, true);
    try {
      await productProblemLinksStore.upsert(link.product_id, link.problem_id, {
        fit_level: link.fit_level,
        notes: next.length > 0 ? next : null,
      });
    } catch (err) {
      linksError = err instanceof Error ? err.message : String(err);
    } finally {
      setBusy(link.problem_id, false);
    }
  }

  // URL から ?tab=sales / ?tab=usage&status=active 等を読み取って初期化
  $effect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const tab = params.get("tab");
    if (isTabKey(tab)) {
      activeTab = tab;
    }
    if (tab === "usage") {
      const status = params.get("status");
      if (
        status === "opportunity" ||
        status === "lost" ||
        status === "active" ||
        status === "cases"
      ) {
        usageSegment = status;
      }
    }
  });

  // usage タブを開いたときに lazy load
  $effect(() => {
    if (
      activeTab === "usage" &&
      productUsageStore.data === null &&
      !productUsageStore.loading
    ) {
      void productUsageStore.load(id);
    }
  });

  let allowEdit = $derived(
    sessionStore.user ? canEditMasters(sessionStore.user.role) : false,
  );
  let excludeIds = $derived(
    productProblemLinksStore.data.map((l) => l.problem_id),
  );

  // InlineField 編集 (catalog No.141-156)。 文字列系 (text/textarea/decimal) は
  // 空入力で null クリア。 PATCH は tri-state を解決する backend へ。
  type ProductStrKey =
    | "name"
    | "summary"
    | "roadmap"
    | "improvement_backlog"
    | "service_site_url"
    | "price_detail";

  async function saveStr(field: ProductStrKey, next: string): Promise<void> {
    const body: ProductPatchRequest = {
      [field]: next.length > 0 ? next : null,
    };
    await patchProduct(id, body);
  }

  $effect(() => {
    void productDetailStore.load(id);
    void problemOptionsStore.load();
    return () => {
      productDetailStore.clear();
      productUsageStore.clear();
      productProblemLinksStore.clear();
      problemOptionsStore.clear();
    };
  });

  $effect(() => {
    if (productDetailStore.data?.id === id) {
      void productProblemLinksStore.load(id);
    }
  });

  async function handleDeleteLink(link: ProductProblemLink) {
    if (!window.confirm("この紐づけを外しますか?")) return;
    try {
      await productProblemLinksStore.delete(link.product_id, link.problem_id);
      linksError = null;
    } catch (err) {
      linksError = err instanceof Error ? err.message : String(err);
    }
  }

  function handleLinkAdded(link: ProductProblemLink, problem?: Problem) {
    addLinkOpen = false;
    // AddProblemLinkModal は API を直接呼ぶため、返却されたリンクを store に反映する。
    if (problem) {
      selectedProblemOptions = [
        problem,
        ...selectedProblemOptions.filter((p) => p.id !== problem.id),
      ];
    }
    productProblemLinksStore.addOrReplace(link);
  }
</script>

<p class="back-link"><a href="/products">← 自社ソリューション一覧へ</a></p>

{#if productDetailStore.loading && productDetailStore.data === null}
  <p class="status">読み込み中…</p>
{:else if productDetailStore.error}
  <p class="status error">{productDetailStore.error}</p>
{:else if productDetailStore.data}
  {@const product = productDetailStore.data}
  <header class="solution-header">
    <div>
      <p class="eyebrow">自社ソリューション</p>
      <h1 data-testid="solution-detail-name">{product.name}</h1>
    </div>
  </header>

  {#if linksError}<p class="status error">{linksError}</p>{/if}
  {#if problemOptionsStore.error}<p class="status error">
      {problemOptionsStore.error}
    </p>{/if}
  {#if productProblemLinksStore.error}<p class="status error">
      {productProblemLinksStore.error}
    </p>{/if}

  <nav class="tabs" data-testid="solution-detail-tabs" aria-label="詳細タブ">
    <button
      type="button"
      class:active={activeTab === "overview"}
      data-testid="solution-tab-overview"
      onclick={() => (activeTab = "overview")}
    >
      概要
    </button>
    <button
      type="button"
      class:active={activeTab === "problems"}
      data-testid="solution-tab-problems"
      onclick={() => (activeTab = "problems")}
    >
      解決する課題 ({productProblemLinksStore.data.length})
    </button>
    <button
      type="button"
      class:active={activeTab === "sales"}
      data-testid="solution-tab-sales"
      onclick={() => (activeTab = "sales")}
    >
      営業支援
    </button>
    <button
      type="button"
      class:active={activeTab === "competitors"}
      data-testid="solution-tab-competitors"
      onclick={() => (activeTab = "competitors")}
    >
      競合ソリューション
    </button>
    <button
      type="button"
      class:active={activeTab === "usage"}
      data-testid="solution-tab-usage"
      onclick={() => (activeTab = "usage")}
    >
      利用状況
    </button>
  </nav>

  {#if activeTab === "overview"}
    <section class="panel" data-testid="solution-section-overview">
      <h2>概要</h2>
      <div class="grid">
        <InlineField
          label="自社ソリューション名"
          value={product.name}
          type="text"
          disabled={!allowEdit}
          onSave={(n) => saveStr("name", n)}
        />
        <InlineField
          label="説明"
          value={product.summary}
          type="textarea"
          disabled={!allowEdit}
          onSave={(n) => saveStr("summary", n)}
        />
        <InlineField
          label="サービスサイト"
          value={product.service_site_url}
          type="text"
          placeholder="https://..."
          disabled={!allowEdit}
          onSave={(n) => saveStr("service_site_url", n)}
        />
        <InlineField
          label="価格"
          value={product.price_detail}
          type="textarea"
          textareaRows={5}
          disabled={!allowEdit}
          onSave={(n) => saveStr("price_detail", n)}
        />
        <InlineField
          label="ロードマップ"
          value={product.roadmap}
          type="textarea"
          disabled={!allowEdit}
          onSave={(n) => saveStr("roadmap", n)}
        />
        <InlineField
          label="プロダクト改善リスト"
          value={product.improvement_backlog}
          type="textarea"
          textareaRows={5}
          disabled={!allowEdit}
          onSave={(n) => saveStr("improvement_backlog", n)}
        />
      </div>
    </section>
  {:else if activeTab === "problems"}
    <section
      class="panel problem-links-editor"
      data-testid="solution-section-problems"
    >
      <header class="panel-header">
        <div>
          <h2>解決する課題</h2>
        </div>
        {#if allowEdit}
          <button
            type="button"
            class="link-problem-button"
            data-testid="solution-problem-link-open"
            onclick={() => (addLinkOpen = true)}
          >
            + 課題を紐づけ
          </button>
        {/if}
      </header>
      {#if productProblemLinksStore.data.length === 0}
        <div class="empty" data-testid="solution-problem-links-empty">
          <p>この自社ソリューションに紐づく課題がありません。</p>
        </div>
      {:else}
        <div class="table-scroll">
          <table class="link-table" data-testid="solution-problem-links-table">
            <thead>
              <tr>
                <th>課題</th>
                <th>備考</th>
                {#if allowEdit}<th class="action-col">操作</th>{/if}
              </tr>
            </thead>
            <tbody>
              {#each productProblemLinksStore.data as link (link.problem_id)}
                {@const itemBusy = busyProblemIds.has(link.problem_id)}
                <tr
                  class:busy={itemBusy}
                  data-testid="solution-problem-link-row"
                >
                  <td>
                    <span
                      class="problem-name"
                      data-testid="solution-problem-link-name"
                    >
                      {problemName(link.problem_id)}
                    </span>
                  </td>
                  <td>
                    {#if allowEdit}
                      <textarea
                        class="notes-input"
                        rows="1"
                        placeholder="備考 (任意)"
                        disabled={itemBusy}
                        data-testid="solution-problem-link-notes"
                        value={notesDraft[link.problem_id] ?? link.notes ?? ""}
                        oninput={(ev) =>
                          (notesDraft = {
                            ...notesDraft,
                            [link.problem_id]: (
                              ev.target as HTMLTextAreaElement
                            ).value,
                          })}
                        onblur={() => commitNotes(link)}
                      ></textarea>
                    {:else}
                      <span class="notes-readonly">{link.notes ?? "—"}</span>
                    {/if}
                  </td>
                  {#if allowEdit}
                    <td class="action-col">
                      <KebabMenu
                        label={`${problemName(link.problem_id)} の操作`}
                        disabled={itemBusy}
                      >
                        <button
                          type="button"
                          class="menu-item danger"
                          onclick={() => handleDeleteLink(link)}
                          data-testid="solution-problem-link-remove"
                        >
                          紐づけを解除
                        </button>
                      </KebabMenu>
                    </td>
                  {/if}
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      {/if}

    </section>
  {:else if activeTab === "sales"}
    <section class="panel" data-testid="solution-section-sales">
      <!-- 資料は product_sales_materials を単一の正本とし、 下のエディタで管理 (C-33)。 -->
      <h2>営業支援</h2>
      <h3 class="sales-subhead">資料</h3>
      <ProductSalesMaterialsEditor productId={product.id} canEdit={allowEdit} />
    </section>
  {:else if activeTab === "competitors"}
    <!-- 競合は構造化テーブル product_competitor_services (competitor_services
         マスタ参照) に一本化。 詳細「競合ソリューション」 タブで編集し、 一覧側には混ぜない。
         サービス名を集約表示する (catalog No.136 / 161-162)。 -->
    <section class="panel" data-testid="solution-section-competitors">
      <ProductCompetitorsEditor
        productId={product.id}
        canEdit={allowEdit}
      />
    </section>
  {:else if activeTab === "usage"}
    <section class="panel" data-testid="solution-section-usage">
      <header class="usage-header">
        <h2>利用状況</h2>
        <div class="usage-tabs" role="tablist">
          <button
            type="button"
            class="usage-tab"
            role="tab"
            aria-selected={usageSegment === "active"}
            class:active={usageSegment === "active"}
            onclick={() => (usageSegment = "active")}
          >
            利用中{productUsageStore.data
              ? ` (${productUsageStore.data.active.length})`
              : ""}
          </button>
          <button
            type="button"
            class="usage-tab"
            role="tab"
            aria-selected={usageSegment === "cases"}
            class:active={usageSegment === "cases"}
            onclick={() => (usageSegment = "cases")}
          >
            事例一覧{productUsageStore.data
              ? ` (${productUsageStore.data.active.length})`
              : ""}
          </button>
          <button
            type="button"
            class="usage-tab"
            role="tab"
            aria-selected={usageSegment === "opportunity"}
            class:active={usageSegment === "opportunity"}
            onclick={() => (usageSegment = "opportunity")}
          >
            商談中{productUsageStore.data
              ? ` (${productUsageStore.data.in_progress.length})`
              : ""}
          </button>
          <button
            type="button"
            class="usage-tab"
            role="tab"
            aria-selected={usageSegment === "lost"}
            class:active={usageSegment === "lost"}
            onclick={() => (usageSegment = "lost")}
          >
            失注{productUsageStore.data
              ? ` (${productUsageStore.data.lost.length})`
              : ""}
          </button>
        </div>
      </header>

      {#if productUsageStore.loading}
        <p class="muted">読み込み中…</p>
      {:else if productUsageStore.error}
        <div class="error">{productUsageStore.error}</div>
      {:else}
        {@const rows = usageRows(productUsageStore.data, usageSegment)}
        {#if rows.length === 0}
          <p class="muted">{usageEmptyMessage(usageSegment)}</p>
        {:else if usageSegment === "lost"}
          <div class="table-scroll">
            <table class="usage-table lost-usage-table">
              <thead>
                <tr>
                  <th>顧客名</th>
                  <th>失注元</th>
                  <th>失注理由</th>
                  <th>失注理由メモ</th>
                  <th>更新日</th>
                  <th>対象弁護士</th>
                  <th>課題タグ</th>
                  <th>AI推認スコア</th>
                  <th>確認状態</th>
                </tr>
              </thead>
              <tbody>
                {#each rows as r, idx (idx)}
                  <tr>
                    <td>
                      <a href={`/firms/${r.firm_id}`} class="primary-link"
                        >{r.firm_name}</a
                      >
                    </td>
                    <td>{lostSourceLabel(r)}</td>
                    <td>{lostReasonLabel(r.lost_reason)}</td>
                    <td class="memo-cell">{r.lost_reason_note ?? "-"}</td>
                    <td>{formatDate(lostDate(r))}</td>
                    <td>{listLabel(r.lawyer_names)}</td>
                    <td>{r.problem_name ?? "-"}</td>
                    <td>{scoreLabel(r.ai_score)}</td>
                    <td>{verificationStatusLabel(r.verification_status)}</td>
                  </tr>
                {/each}
              </tbody>
            </table>
          </div>
        {:else}
          <table class="usage-table">
            <thead>
              <tr>
                <th>法律事務所</th>
                {#if showOpportunityColumn(usageSegment)}
                  <th>商談名</th>
                {/if}
                <th>価格</th>
                <th>開始日</th>
                <th>終了日</th>
              </tr>
            </thead>
            <tbody>
              {#each rows as r, idx (idx)}
                <tr>
                  <td>
                    <a href={`/firms/${r.firm_id}`} class="primary-link"
                      >{r.firm_name}</a
                    >
                  </td>
                  {#if showOpportunityColumn(usageSegment)}
                    <td>{r.opportunity_name ?? "-"}</td>
                  {/if}
                  <td>{formatPrice(r.price)}</td>
                  <td>{formatDate(r.starts_on)}</td>
                  <td>{formatDate(r.ends_on)}</td>
                </tr>
              {/each}
            </tbody>
          </table>
        {/if}
      {/if}
    </section>
  {/if}

  <AddProblemLinkModal
    open={addLinkOpen}
    productId={product.id}
    {excludeIds}
    onClose={() => (addLinkOpen = false)}
    onUpserted={handleLinkAdded}
  />
{/if}

<style>
  .back-link {
    margin: 0 0 8px;
  }
  .back-link a {
    color: #2563eb;
    text-decoration: none;
    font-size: 0.875rem;
  }
  .back-link a:hover {
    text-decoration: underline;
  }
  .solution-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 24px;
    margin-bottom: 16px;
    padding: 16px;
    background: #fff;
    border: 1px solid #dfe3ea;
    border-radius: 8px;
  }
  .eyebrow {
    margin: 0 0 4px;
    color: #6b7280;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
  }
  h1 {
    margin: 0;
    font-size: 22px;
  }
  .status {
    color: #6b7280;
  }
  .status.error {
    color: #b91c1c;
  }
  .tabs {
    display: flex;
    border-bottom: 1px solid #e5e7eb;
    margin-bottom: 16px;
    gap: 4px;
    flex-wrap: wrap;
  }
  .tabs button {
    border: 0;
    border-radius: 0;
    padding: 8px 16px;
    background: transparent;
    color: #6b7280;
    border-bottom: 2px solid transparent;
    margin-bottom: -1px;
    cursor: pointer;
    font: inherit;
  }
  .tabs button.active {
    color: #2563eb;
    border-bottom-color: #2563eb;
  }
  .panel {
    background: #fff;
    border: 1px solid #dfe3ea;
    border-radius: 8px;
    padding: 18px 20px;
    margin-top: 12px;
  }
  .panel h2 {
    margin: 0 0 12px;
    font-size: 18px;
  }
  .sales-subhead {
    margin: 20px 0 10px;
    font-size: 0.95rem;
    font-weight: 700;
    color: #374151;
  }
  .panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
  }
  .panel-header h2 {
    margin: 0;
  }
  .muted {
    color: #6b7280;
  }
  /* InlineField を 2-column inline grid で並べる (LawyerDetail / ProblemDetail と同形) */
  .grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 6px 16px;
  }
  .grid :global(.inline-field) {
    display: grid;
    grid-template-columns: 200px 1fr;
    align-items: baseline;
    gap: 4px 16px;
    padding: 4px 0;
    border-bottom: 1px solid #f3f4f6;
  }
  .grid :global(.inline-field > .field-label) {
    font-size: 0.75rem;
    color: #6b7280;
    font-weight: 700;
  }
  .table-scroll {
    overflow-x: auto;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.875rem;
  }
  th,
  td {
    text-align: left;
    padding: 8px 12px;
    border-bottom: 1px solid #e5e7eb;
  }
  th {
    color: #4b5563;
    background: #f9fafb;
    font-weight: 600;
  }
  .usage-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
  }
  .usage-tabs {
    display: inline-flex;
    border: 1px solid #d1d5db;
    border-radius: 999px;
    overflow: hidden;
  }
  .usage-tab {
    background: #ffffff;
    border: 0;
    padding: 6px 14px;
    font: inherit;
    font-size: 13px;
    color: #4b5563;
    cursor: pointer;
  }
  .usage-tab + .usage-tab {
    border-left: 1px solid #d1d5db;
  }
  .usage-tab.active {
    background: #2563eb;
    color: #ffffff;
  }
  .usage-table {
    width: 100%;
    border-collapse: collapse;
  }
  .usage-table th,
  .usage-table td {
    border-bottom: 1px solid #e5e7eb;
    padding: 8px 12px;
    text-align: left;
    font-size: 13px;
  }
  .usage-table th {
    background: #f9fafb;
    font-weight: 600;
    color: #374151;
  }
  .lost-usage-table {
    min-width: 960px;
  }
  .lost-usage-table .memo-cell {
    max-width: 260px;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }
  .primary-link {
    color: #2563eb;
    text-decoration: none;
  }
  .primary-link:hover {
    text-decoration: underline;
  }

  /* 解決する課題 (課題リンク + 備考 + 候補リスト) のスタイル。 */
  .problem-links-editor .panel-header {
    gap: 12px;
    align-items: flex-start;
    flex-wrap: wrap;
  }
  .link-problem-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 32px;
    padding: 6px 12px;
    border: 1px solid #1d4ed8;
    background: #1d4ed8;
    color: #fff;
    border-radius: 6px;
    font: inherit;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.2;
    cursor: pointer;
  }
  .link-problem-button:hover {
    background: #1e40af;
  }
  .problem-links-editor .empty {
    display: grid;
    justify-items: center;
    gap: 12px;
    margin: 0;
    padding: 24px;
    text-align: center;
    color: #6b7280;
    background: #f9fafb;
    border: 1px dashed #e5e7eb;
    border-radius: 6px;
  }
  .problem-links-editor .empty p {
    margin: 0;
  }
  .problem-links-editor .problem-name {
    font-weight: 500;
    color: #111827;
  }
  .problem-links-editor tr.busy {
    opacity: 0.6;
  }
  .problem-links-editor .notes-input {
    width: 100%;
    padding: 6px 8px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font: inherit;
    font-size: 0.8125rem;
    line-height: 1.4;
    min-height: 32px;
    resize: vertical;
  }
  .notes-readonly {
    display: block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #4b5563;
  }
  .action-col {
    white-space: nowrap;
    text-align: right;
    width: 1%;
  }
  @media (max-width: 720px) {
    .link-problem-button {
      width: 100%;
    }
  }
</style>
